import {
  getCurrentBrowserUrl,
  isPublicRoute,
  type ApiClientConfig,
} from "@brewardapp/api-client";
import { store, useStore } from "@breward/store";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export type SessionExpiredScope =
  | "ParticipantWeb"
  | "JudgeWeb"
  | "StaffWeb";

interface UseApiClientConfigOptions {
  apiUrl: string;
  /** Picks the `Notifications:<scope>.sessionExpired.*` translation keys. */
  scope: SessionExpiredScope;
}

/**
 * Builds a stable `ApiClientConfig` shared by participant-web, judge-web and
 * staff-web.
 *
 * The identity of the returned object never changes, which is the whole point:
 * `ApiClientProvider` rebuilds the api client whenever the config changes, and a
 * config keyed on `authToken` meant every login swapped the client mid-flight.
 * The token is therefore read live from the store on each request instead of
 * being captured in a closure.
 */
export const useApiClientConfig = ({
  apiUrl,
  scope,
}: UseApiClientConfigOptions): ApiClientConfig => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [t] = useTranslation();

  const logoutUser = useStore((state) => state.logoutUser);
  const showNotification = useStore((state) => state.showNotification);

  const handleUnauthorized = useCallback(
    (backToUrl?: string) => {
      // Login and password reset are reachable without a session. A 401 there
      // only means a leftover token is stale - clear it quietly, never toast or
      // redirect, or the user loses the reset link they opened from their inbox.
      if (isPublicRoute()) {
        logoutUser?.(undefined);
        return;
      }

      const currentUrl = backToUrl || getCurrentBrowserUrl();

      logoutUser?.(currentUrl);
      queryClient.clear();

      showNotification?.({
        type: "error",
        title: t(
          `Notifications:${scope}.sessionExpired.title`,
          "Session expired"
        ),
        message: t(
          `Notifications:${scope}.sessionExpired.message`,
          "Your session has expired. Please sign in again."
        ),
      });

      navigate("/login", { replace: true });
    },
    [logoutUser, navigate, queryClient, scope, showNotification, t]
  );

  // Kept behind a ref so a new `t` (language switch) or a new `navigate` cannot
  // change the config identity and force the api client to be rebuilt.
  const handleUnauthorizedRef = useRef(handleUnauthorized);
  handleUnauthorizedRef.current = handleUnauthorized;

  return useMemo(
    () => ({
      apiUrl,
      getAuthToken: () => store.getState().authToken ?? null,
      onUnauthorized: (backToUrl?: string) =>
        handleUnauthorizedRef.current(backToUrl),
    }),
    [apiUrl]
  );
};
